<template>
    <v-container fluid class="pa-4">
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between flex-wrap ga-2">
                <div>
                    <h1 class="text-h5 font-weight-bold">Alert History</h1>
                    <p class="text-body-2 text-medium-emphasis mt-1">
                        {{ isAdmin ? 'Alerts sent to the users of a company' : 'Every alert sent to you, newest first' }}
                    </p>
                </div>
                <div class="d-flex align-center ga-2">
                    <v-btn icon="mdi-refresh" variant="text" aria-label="Refresh"
                           :disabled="isAdmin && !selectedCompany" :loading="loading"
                           @click="reload"/>
                    <template v-if="!isAdmin">
                        <v-btn color="primary" prepend-icon="mdi-email-open-outline" size="small" variant="tonal"
                               @click="bulkAction = 'read'">Mark all read
                        </v-btn>
                        <v-btn color="error" prepend-icon="mdi-delete-sweep-outline" size="small" variant="tonal"
                               @click="bulkAction = 'clear'">Clear all
                        </v-btn>
                    </template>
                </div>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col v-if="isAdmin" cols="12" md="4" sm="6">
                <company-select v-model="selectedCompany" :items="companies" item-text="company_name"
                                label="Select Company"
                                @update:model-value="reload"/>
            </v-col>
            <template v-else>
                <v-col cols="12" md="2" sm="6">
                    <v-select v-model="selectedAlertType" :items="alertTypes" item-title="label" item-value="value"
                              label="Alert type" clearable density="compact"
                              @update:model-value="onFilterChange"/>
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete v-model="selectedAsset" :items="assets" item-title="label" item-value="id"
                                    label="Vehicle" clearable density="compact"
                                    @update:model-value="onFilterChange"/>
                </v-col>
                <v-col cols="12" md="2" sm="6">
                    <v-text-field v-model="fromDate" label="From" type="datetime-local" clearable density="compact"
                                  @update:model-value="onFilterChange"/>
                </v-col>
                <v-col cols="12" md="2" sm="6">
                    <v-text-field v-model="toDate" label="To" type="datetime-local" clearable density="compact"
                                  @update:model-value="onFilterChange"/>
                </v-col>
                <v-col cols="12" md="3" sm="6" class="d-flex align-center">
                    <v-switch v-model="showUnreadOnly" label="Unread only" color="error" density="compact" hide-details
                              @update:model-value="onFilterChange"/>
                </v-col>
            </template>
        </v-row>

        <!-- Loading -->
        <div v-if="loading" class="mt-2" aria-busy="true">
            <v-skeleton-loader v-for="i in 6" :key="i" type="list-item-avatar-two-line" class="mb-1"/>
        </div>

        <!-- Empty -->
        <div v-else-if="!notifications.length" class="d-flex flex-column align-center pa-12 text-center">
            <v-icon icon="mdi-bell-sleep-outline" size="64" color="grey-lighten-2"/>
            <p class="text-body-1 text-medium-emphasis mt-3">
                {{ isAdmin && !selectedCompany ? 'Select a company to see its alerts' : 'No alerts found' }}
            </p>
            <template v-if="hasFilters">
                <p class="text-caption text-medium-emphasis">Try removing the active filters</p>
                <v-btn variant="tonal" size="small" class="mt-3" @click="clearFilters">Clear filters</v-btn>
            </template>
        </div>

        <!-- Phones and tablets: the same cards as Activity -->
        <div v-else-if="!mdAndUp">
            <ActivityAlertCard v-for="notification in notifications" :key="notification.id"
                               :notification="notification" :now-tick="nowTick"
                               :show-delivery="isAdmin"
                               @toggle-read="toggleRead" @delivery="openDelivery"/>
        </div>

        <v-table v-else class="alert-table" hover>
            <thead>
            <tr>
                <th v-if="isAdmin">User</th>
                <th>Vehicle</th>
                <th>Alert</th>
                <th-btn :sortedColumn="sortedColumn" column="event_timestamp" sortable title="Received" @sort="sort"/>
                <th class="text-end">{{ isAdmin ? 'Read' : '' }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="notification in notifications" :key="notification.id"
                class="text-left alert-row" :class="severityClass(notification)"
                tabindex="0" @click="openDetails(notification)" @keydown.enter.self="openDetails(notification)">
                <td v-if="isAdmin">{{ notification.user }}</td>
                <td class="text-no-wrap">{{ notification.asset || 'Vehicle' }}</td>
                <td class="py-2">
                    <div class="d-flex align-center ga-3">
                        <v-avatar :color="alertColor(notification)" size="32" variant="tonal" class="flex-shrink-0">
                            <v-icon :icon="alertIcon(notification)" size="18"/>
                        </v-avatar>
                        <div class="min-w-0">
                            <div class="d-flex align-center ga-1 flex-wrap">
                                <v-icon v-if="!isAdmin && !notification.read" icon="mdi-circle" size="8" color="error"
                                        aria-label="Unread"/>
                                <span :class="!isAdmin && !notification.read ? 'font-weight-bold' : 'font-weight-medium'">
                                    {{ alertHeadline(notification) || notification.title }}
                                </span>
                                <AlertSeverityChip :alert="notification"/>
                            </div>
                            <div class="text-caption text-medium-emphasis">
                                {{ alertLabel(notification) }}<template v-if="alertDetail(notification)"> · {{ alertDetail(notification) }}</template>
                            </div>
                        </div>
                    </div>
                </td>
                <td class="text-no-wrap">
                    <div>{{ agoOf(notification) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ formatCompanyDateTime(notification.event_timestamp) }}</div>
                </td>
                <td class="text-end text-no-wrap">
                    <template v-if="!isAdmin">
                        <v-tooltip location="top" :text="notification.read ? 'Mark unread' : 'Mark read'">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" :aria-label="notification.read ? 'Mark unread' : 'Mark read'"
                                       :icon="notification.read ? 'mdi-email-outline' : 'mdi-email-open-outline'"
                                       size="small" variant="text" color="medium-emphasis"
                                       @click.stop="toggleRead(notification)"/>
                            </template>
                        </v-tooltip>
                    </template>
                    <template v-else>
                        <v-icon :icon="notification.read ? 'mdi-email-open' : 'mdi-email'" size="small"
                                :aria-label="notification.read ? 'Read' : 'Unread'"/>
                        <v-tooltip location="top" text="Delivery details">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" icon="mdi-send-check-outline" aria-label="Delivery details"
                                       size="small" variant="text" color="medium-emphasis" class="ms-1"
                                       @click.stop="openDelivery(notification)"/>
                            </template>
                        </v-tooltip>
                    </template>
                </td>
            </tr>
            </tbody>
        </v-table>

        <div v-if="totalPages > 1 && !loading" class="d-flex justify-center">
            <v-pagination v-model="currentPage" :length="totalPages" class="my-4" density="compact"
                          @update:model-value="fetchNotification"/>
        </div>
    </v-container>

    <v-dialog v-model="dialogSwitch" max-width="700" scrollable>
        <v-card v-if="selectedNotification">
            <v-card-title class="d-flex align-center pa-3">
                <span class="text-body-1 font-weight-bold">Alert Details</span>
                <v-spacer/>
                <v-btn icon="mdi-close" aria-label="Close" variant="text" size="small" @click="dialogSwitch = false"/>
            </v-card-title>
            <v-divider/>
            <v-card-text class="pa-0">
                <alert-payload-details :notification="selectedNotification"/>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Admin only: which channels the alert went out on -->
    <alert-details v-if="alertDetailsDialog" v-model="alertDetailsDialog" :alert="selectedNotification"/>

    <AlertsBulkConfirm :model-value="!!bulkAction" :action="bulkAction || 'read'" :loading="bulkBusy"
                       :vehicle-name="bulkVehicleName" :ignored-filters="bulkIgnoredFilters"
                       @update:model-value="(v) => { if (!v) bulkAction = null; }"
                       @confirm="runBulkAction"/>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useDisplay} from "vuetify";
import {send} from "@/composables/apiClient";
import {useAutoMarkRead} from "@/composables/useAutoMarkRead";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import AlertDetails from "@/components/dialogs/assets/AlertDetails.vue";
import AlertsBulkConfirm from "@/components/dialogs/assets/AlertsBulkConfirm.vue";
import {companyTimezone, formatCompanyDateTime, parseApiDate, zonedInputToUtcIso} from "@/composables/useTimezone";
import AlertPayloadDetails from "@/components/cards/AlertPayloadDetails.vue";
import ActivityAlertCard from "@/components/cards/ActivityAlertCard.vue";
import AlertSeverityChip from "@/components/cards/AlertSeverityChip.vue";
import CompanySelect from "@/components/globals/company-select.vue";
import {
    ALERT_TYPE_FILTER_OPTIONS,
    alertColor,
    alertDetail,
    alertHeadline,
    alertIcon,
    alertLabel,
    alertSeverity,
} from "@/composables/alertTypes";

const {user} = useAuthStore();
const isAdmin = user.type === 'admin';
const {mdAndUp} = useDisplay();
const {showSnackbar} = useSnackbarStore();
const route = useRoute();
const router = useRouter();

const totalPages = ref(1);
const companies = ref([]);
// Built locally rather than from the response's alert_types, which carries
// enum-name labels and the types that never fire (4, 11, 12).
const alertTypes = ALERT_TYPE_FILTER_OPTIONS;
const assets = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const sortedType = ref(null);
const notifications = ref([]);
const sortedColumn = ref(null);
const dialogSwitch = ref(false);
const selectedCompany = ref(null);
const alertDetailsDialog = ref(false);
const selectedNotification = ref(null);
const bulkAction = ref(null);
const bulkBusy = ref(false);
const nowTick = ref(Date.now());

// Filters live in the URL so a link, a reload or Back keeps them. The map
// marker links here with ?asset=X&unread=1.
const queryInt = (v) => (v ? parseInt(v, 10) || null : null);
const filtersFromQuery = (q) => ({
    alertType: queryInt(q.alert_type),
    asset: queryInt(q.asset),
    from: q.from || null,
    to: q.to || null,
    unread: q.unread === '1',
});
const initial = filtersFromQuery(route.query);
const selectedAlertType = ref(initial.alertType);
const selectedAsset = ref(initial.asset);
const fromDate = ref(initial.from);
const toDate = ref(initial.to);
const showUnreadOnly = ref(initial.unread);

// Back and forward change the URL without touching the fields; apply it.
watch(() => route.query, (q) => {
    if (route.name !== 'notifications') return;
    const f = filtersFromQuery(q);
    const same = f.alertType === selectedAlertType.value && f.asset === selectedAsset.value
        && f.from === (fromDate.value || null) && f.to === (toDate.value || null)
        && f.unread === showUnreadOnly.value;
    if (same) return;
    selectedAlertType.value = f.alertType;
    selectedAsset.value = f.asset;
    fromDate.value = f.from;
    toDate.value = f.to;
    showUnreadOnly.value = f.unread;
    clearTimeout(filterTimer);
    reload();
});

// The date fields are wall clock times in the company timezone.
const toUtc = (v) => (v ? zonedInputToUtcIso(v, companyTimezone()) : null);

const hasFilters = computed(() => !isAdmin && !!(selectedAlertType.value || selectedAsset.value
    || fromDate.value || toDate.value || showUnreadOnly.value));

const severityClass = (n) => ({critical: 'row-critical', high: 'row-high'})[alertSeverity(n)] ?? '';

const agoOf = (n) => {
    const ms = parseApiDate(n?.event_timestamp)?.getTime();
    if (!ms) return '';
    const m = Math.floor((nowTick.value - ms) / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return d === 1 ? 'Yesterday' : `${d} days ago`;
};

const writeQuery = () => {
    const query = {...route.query};
    const set = (k, v) => {
        if (v === null || v === undefined || v === '' || v === false) delete query[k];
        else query[k] = String(v);
    };
    set('alert_type', selectedAlertType.value);
    set('asset', selectedAsset.value);
    set('from', fromDate.value);
    set('to', toDate.value);
    set('unread', showUnreadOnly.value ? '1' : null);
    router.replace({query});
};

// Typing a date fires on every keystroke, so filter changes settle first.
let filterTimer = null;
const onFilterChange = () => {
    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
        writeQuery();
        reload();
    }, 350);
};

const clearFilters = () => {
    selectedAlertType.value = null;
    selectedAsset.value = null;
    fromDate.value = null;
    toDate.value = null;
    showUnreadOnly.value = false;
    clearTimeout(filterTimer);
    writeQuery();
    reload();
};

// Opening this screen reads the viewer's notifications. Admin viewers browse
// other users' rows, which the API will not mark, so they are skipped. With
// "Unread only" on nothing is marked automatically: the user came to see those
// alerts and marks them, and the pages stay the same while they do.
const {attempt: autoMarkRead} = useAutoMarkRead({
    enabled: () => !isAdmin && !showUnreadOnly.value,
    hasUnread: () => notifications.value.some(n => !n.read),
    onMarked: () => {
        notifications.value.forEach(n => {
            n.read = true;
        });
    },
});

let tickTimer = null;
onMounted(() => {
    tickTimer = setInterval(() => { nowTick.value = Date.now(); }, 60000);
    if (isAdmin) {
        fetchCompaniesList();
    } else {
        fetchAssetsList();
        fetchNotification();
    }
});
onUnmounted(() => {
    clearInterval(tickTimer);
    clearTimeout(filterTimer);
    fetchRequestId++;
});

const fetchAssetsList = () => {
    send('get', `assets/list`)
        .then((response) => {
            assets.value = response.data.data;
        })
        .catch(() => {});
};

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Companies list could not be loaded', 'error');
        });
};

let fetchRequestId = 0;
const fetchNotification = () => {
    if (isAdmin && !selectedCompany.value) {
        notifications.value = [];
        totalPages.value = 1;
        return;
    }
    const requestId = ++fetchRequestId;
    const data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        company: isAdmin ? selectedCompany.value : user.company.id,
        alert_type: selectedAlertType.value,
        asset: selectedAsset.value,
        from_date: toUtc(fromDate.value),
        to_date: toUtc(toDate.value),
        unread: showUnreadOnly.value ? 1 : undefined,
    };
    loading.value = true;
    send('get', `assets/notifications`, data)
        .then((response) => {
            if (requestId !== fetchRequestId) return;
            const resData = response.data.data;
            // Handle different response structures (with/without asset filter)
            const list = resData.notifications ?? resData;
            notifications.value = list.data ?? [];
            totalPages.value = list.meta?.last_page ?? 1;
            currentPage.value = list.meta?.current_page ?? 1;
            // The list the user is now looking at has loaded.
            autoMarkRead();
        })
        .catch(() => {
            if (requestId !== fetchRequestId) return;
            showSnackbar('Alerts could not be loaded', 'error');
        })
        .finally(() => {
            if (requestId === fetchRequestId) loading.value = false;
        });
};

const reload = () => {
    currentPage.value = 1;
    fetchNotification();
};

const toggleRead = (notification) => {
    send('put', `notifications/${notification.id}`)
        .then((response) => {
            if (!response.data.success || !response.data.data) return;
            const row = notifications.value.find(item => item.id === notification.id);
            if (row) row.read = !row.read;
        })
        .catch(() => {
            showSnackbar('Could not update this alert', 'error');
        });
};

// The vehicle filter limits both bulk calls; the API cannot apply the others.
const bulkVehicleName = computed(() => (selectedAsset.value
    ? (assets.value.find(a => a.id === selectedAsset.value)?.label || 'this vehicle')
    : null));
const bulkIgnoredFilters = computed(() => {
    const parts = [];
    if (selectedAlertType.value) parts.push('alert type');
    if (fromDate.value || toDate.value) parts.push('date');
    if (showUnreadOnly.value) parts.push('unread only');
    if (!parts.length) return null;
    return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
});

// Both calls act on the viewer's own alerts only.
const runBulkAction = async () => {
    const action = bulkAction.value;
    const assetId = selectedAsset.value || undefined;
    bulkBusy.value = true;
    try {
        if (action === 'clear') {
            await send('delete', `notifications/clear`, {params: {asset_id: assetId}});
            showSnackbar('Your alerts were cleared');
        } else {
            await send('post', `notifications/read`, {asset_id: assetId});
            showSnackbar('Alerts marked as read');
        }
        bulkAction.value = null;
        reload();
    } catch {
        showSnackbar(action === 'clear' ? 'Could not clear alerts' : 'Could not mark all as read', 'error');
    } finally {
        bulkBusy.value = false;
    }
};

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchNotification();
};

// Opening an alert reads it, like the Activity cards.
const openDetails = (notification) => {
    selectedNotification.value = notification;
    dialogSwitch.value = true;
    if (!isAdmin && !notification.read) toggleRead(notification);
};

const openDelivery = (notification) => {
    selectedNotification.value = notification;
    alertDetailsDialog.value = true;
};
</script>

<style scoped>
.min-w-0 {
    min-width: 0;
}
.alert-row {
    cursor: pointer;
}
.alert-row:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
}
.row-critical > td {
    background-color: rgba(var(--v-theme-error), 0.10);
}
.row-critical > td:first-child {
    box-shadow: inset 4px 0 0 rgb(var(--v-theme-error));
}
.row-high > td {
    background-color: rgba(var(--v-theme-warning), 0.10);
}
.row-high > td:first-child {
    box-shadow: inset 4px 0 0 rgb(var(--v-theme-warning));
}
</style>
